import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { googleSignIn } from '../../utils/googleAuth';
import * as Apple from 'expo-apple-authentication';
import { Ionicons, FontAwesome } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from '../../context/AuthContext';

const API_BASE_URL = 'https://api.jobeng.ai';


export default function LearningSignupScreen({ navigation }) {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    studentEmail: '',
    educationLevel: '',
    interests: '',
    learningGoals: '',
    password: '',
    confirmPassword: '',
  });
  const [isFocused, setIsFocused] = useState({
    fullName: false,
    studentEmail: false,
    educationLevel: false,
    interests: false,
    learningGoals: false,
    password: false,
    confirmPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [appleAuthAvailable, setAppleAuthAvailable] = useState(false);




  // Check Apple Auth Availability
  React.useEffect(() => {
    checkAppleAuthAvailability();
  }, []);

  // ✅ NEW: Check Apple Authentication Availability
  const checkAppleAuthAvailability = async () => {
    try {
      const isAvailable = await Apple.isAvailableAsync();
      setAppleAuthAvailable(isAvailable);
      console.log('✅ Apple Auth availability for signup:', isAvailable);
    } catch (error) {
      console.error('Error checking Apple auth availability:', error);
      setAppleAuthAvailable(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFocus = (field) => {
    setIsFocused(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const handleBlur = (field) => {
    setIsFocused(prev => ({
      ...prev,
      [field]: false
    }));
  };

  const validateForm = () => {
    const { fullName, studentEmail, educationLevel, interests, learningGoals, password, confirmPassword } = formData;

    if (!fullName.trim()) {
      Alert.alert('Validation Error', 'Please enter your full name');
      return false;
    }

    if (fullName.trim().length < 2) {
      Alert.alert('Validation Error', 'Full name must be at least 2 characters long');
      return false;
    }

    if (!studentEmail.trim()) {
      Alert.alert('Validation Error', 'Please enter your student email');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(studentEmail)) {
      Alert.alert('Validation Error', 'Please enter a valid email address');
      return false;
    }

    if (!educationLevel.trim()) {
      Alert.alert('Validation Error', 'Please enter your education level');
      return false;
    }

    if (!interests.trim()) {
      Alert.alert('Validation Error', 'Please enter your interests');
      return false;
    }

    if (!learningGoals.trim()) {
      Alert.alert('Validation Error', 'Please enter your learning goals');
      return false;
    }

    if (!password.trim()) {
      Alert.alert('Validation Error', 'Please enter a password');
      return false;
    }

    if (password.length < 6) {
      Alert.alert('Validation Error', 'Password must be at least 6 characters long');
      return false;
    }

    if (password !== confirmPassword) {
      Alert.alert('Validation Error', 'Passwords do not match');
      return false;
    }

    if (!agreeToTerms) {
      Alert.alert('Validation Error', 'Please agree to the Terms of Service and Privacy Policy');
      return false;
    }

    return true;
  };

  const handleSignup = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // ✅ FIXED: Proper signup data structure for learning users
      const signupData = {
        full_name: formData.fullName.trim(),
        email: formData.studentEmail.trim().toLowerCase(),
        password: formData.password.trim(),
        user_type: 'learning', // Use learning type directly
        // Add learning-specific data
        education_level: formData.educationLevel.trim(),
        interests: formData.interests.trim(),
        learning_goals: formData.learningGoals.trim(),
        profile_type: 'learning'
      };

      console.log('Attempting learning signup:', {
        ...signupData,
        password: '***hidden***'
      });

      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(signupData),
      });

      const responseText = await response.text();
      console.log('Signup response status:', response.status);
      console.log('Raw signup response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Invalid response format from server');
      }

      console.log('Signup response:', data);

      if (response.ok && data.success) {
        // Parse response data
        let userData = null;
        let token = null;

        if (data.data && data.data.user) {
          userData = data.data.user;
          token = data.data.token;
        } else if (data.user) {
          userData = data.user;
          token = data.token;
        }

        if (!userData) {
          // Create user data from form if not returned
          userData = {
            id: Date.now().toString(),
            email: formData.studentEmail.trim().toLowerCase(),
            full_name: formData.fullName.trim(),
            name: formData.fullName.trim(),
            user_type: 'learning',
            education_level: formData.educationLevel.trim(),
            interests: formData.interests.trim(),
            learning_goals: formData.learningGoals.trim(),
            profile_type: 'learning'
          };
        }

        // Ensure user type is learning
        userData.user_type = 'learning';
        userData.profile_type = 'learning';

        if (token) {
          await AsyncStorage.setItem('auth_token', token);
          await AsyncStorage.setItem('user_data', JSON.stringify(userData));
        }

        // Store learning-specific data
        await AsyncStorage.setItem('learning_profile', JSON.stringify({
          education_level: formData.educationLevel.trim(),
          interests: formData.interests.trim(),
          learning_goals: formData.learningGoals.trim(),
          signup_date: new Date().toISOString()
        }));

        if (login && token) {
          await login(userData, token);
        }

        Alert.alert(
          'Welcome to JobEng Learning!',
          'Your account has been created successfully. Start your learning journey now!',
          [
            {
              text: 'Get Started',
              onPress: () => navigation.navigate('WelcomeLearningScreen')
            }
          ]
        );

      } else {
        let errorMessage = 'Signup failed. Please try again.';
        
        if (data && data.error) {
          errorMessage = data.error;
        } else if (data && data.message) {
          errorMessage = data.message;
        } else if (response.status === 409) {
          errorMessage = 'An account with this email already exists.';
        } else if (response.status === 422) {
          errorMessage = 'Please check your input data.';
        }

        Alert.alert('Signup Error', errorMessage);
      }

    } catch (error) {
      console.error('Learning signup error:', error);
      
      let userMessage = 'Signup failed. Please try again.';
      
      if (error.message.includes('Network request failed')) {
        userMessage = 'Network error. Please check your internet connection.';
      } else if (error.message) {
        userMessage = error.message;
      }

      Alert.alert('Signup Error', userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Google Auth Success Handler for Signup
  const handleGoogleAuthSuccess = async (authResult) => {
    setIsLoading(true);
    try {
      console.log('🔐 Processing Google auth success for learning signup');

      if (!authResult || !authResult.accessToken) {
        throw new Error('Invalid Google authentication result');
      }

      // First try to get user info from Google
      let userInfo = null;
      try {
        const userInfoResponse = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${authResult.accessToken}`);
        userInfo = await userInfoResponse.json();
      } catch (userInfoError) {
        console.warn('Could not fetch Google user info:', userInfoError);
      }

      // Try server registration first
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/google-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: authResult.accessToken,
            id_token: authResult.idToken,
            user_info: userInfo,
            user_type: 'learning',
            is_signup: true
          }),
        });

        const result = await response.json();

        if (result.success) {
          // Store authentication data
          await AsyncStorage.setItem('auth_token', result.data.token);
          await AsyncStorage.setItem('user_data', JSON.stringify(result.data.user));

          // Store learning profile with Google data
          await AsyncStorage.setItem('learning_profile', JSON.stringify({
            education_level: 'Not specified',
            interests: 'General learning',
            learning_goals: 'Explore engineering topics',
            signup_date: new Date().toISOString(),
            signup_method: 'google'
          }));

          console.log('✅ Google signup successful for learning');
          
          Alert.alert(
            'Welcome to JobEng Learning!',
            'Your account has been created with Google. Complete your learning profile to get started!',
            [
              {
                text: 'Complete Profile',
                onPress: () => navigation.navigate('WelcomeLearningScreen')
              }
            ]
          );
          return; // Success, exit function
        }
      } catch (serverError) {
        console.warn('Server Google signup failed, using fallback:', serverError);
      }

      // ✅ FALLBACK: If server registration fails, create local session
      if (userInfo && userInfo.email) {
        const fallbackUser = {
          id: userInfo.id || Date.now().toString(),
          email: userInfo.email,
          name: userInfo.name || 'Google Learning User',
          full_name: userInfo.name || 'Google Learning User',
          picture: userInfo.picture,
          user_type: 'learning',
          profile_type: 'learning',
          signup_method: 'google'
        };

        // Store fallback authentication data
        await AsyncStorage.setItem('auth_token', 'google_learning_signup_' + Date.now());
        await AsyncStorage.setItem('user_data', JSON.stringify(fallbackUser));

        // Store learning profile
        await AsyncStorage.setItem('learning_profile', JSON.stringify({
          education_level: 'Not specified',
          interests: 'General learning',
          learning_goals: 'Explore engineering topics',
          signup_date: new Date().toISOString(),
          signup_method: 'google'
        }));

        console.log('✅ Google fallback signup successful for learning');
        
        Alert.alert(
          'Welcome to JobEng Learning!',
          'Your account has been created with Google. Complete your learning profile to get started!',
          [
            {
              text: 'Complete Profile',
              onPress: () => navigation.navigate('WelcomeLearningScreen')
            }
          ]
        );
      } else {
        throw new Error('Unable to get user information from Google');
      }

    } catch (error) {
      console.error('❌ Google auth signup error:', error);
      Alert.alert('Google Signup Error', 'Unable to complete Google signup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Google Signup Handler
  const handleGoogleSignup = async () => {
    try {
      setIsLoading(true);
      console.log('🔐 Google signup attempt for learning');


      // Native Google Sign-In (returns { accessToken, idToken })
      const authentication = await googleSignIn();
      await handleGoogleAuthSuccess(authentication);

    } catch (error) {
      if (error?.cancelled) return;
      console.error('❌ Google signup error:', error);
      Alert.alert('Google Signup Error', 'Unable to signup with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ NEW: Apple Signup Implementation
  const handleAppleSignup = async () => {
    if (!appleAuthAvailable) {
      Alert.alert('Apple Signup', 'Apple Sign In is not available on this device');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🍎 Apple signup attempt for learning');

      const credential = await Apple.signInAsync({
        requestedScopes: [
          Apple.AppleAuthenticationScope.FULL_NAME,
          Apple.AppleAuthenticationScope.EMAIL,
        ],
      });

      if (credential.identityToken) {
        console.log('🍎 Apple credential received for signup:', {
          user: credential.user,
          email: credential.email,
          fullName: credential.fullName,
          identityToken: credential.identityToken ? 'present' : 'missing'
        });

        // Try server registration first
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/apple-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              identity_token: credential.identityToken,
              authorization_code: credential.authorizationCode,
              user_type: 'learning',
              email: credential.email,
              full_name: credential.fullName ? 
                `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
                null,
              is_signup: true,
              user_info: {
                id: credential.user,
                email: credential.email,
                name: credential.fullName ? 
                  `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
                  'Apple Learning User',
                firstName: credential.fullName?.givenName,
                lastName: credential.fullName?.familyName
              }
            }),
          });

          const result = await response.json();

          if (result.success) {
            // Store authentication data
            await AsyncStorage.setItem('auth_token', result.data.token);
            await AsyncStorage.setItem('user_data', JSON.stringify(result.data.user));

            // Store learning profile with Apple data
            await AsyncStorage.setItem('learning_profile', JSON.stringify({
              education_level: 'Not specified',
              interests: 'General learning',
              learning_goals: 'Explore engineering topics',
              signup_date: new Date().toISOString(),
              signup_method: 'apple'
            }));

            console.log('✅ Apple server signup successful for learning');
            
            Alert.alert(
              'Welcome to JobEng Learning!',
              'Your account has been created with Apple. Complete your learning profile to get started!',
              [
                {
                  text: 'Complete Profile',
                  onPress: () => navigation.navigate('WelcomeLearningScreen')
                }
              ]
            );
            return; // Success, exit function
          }
        } catch (serverError) {
          console.warn('Server Apple signup failed, using fallback:', serverError);
        }

        // ✅ FALLBACK: If server registration fails, create local session
        const fallbackUser = {
          id: credential.user,
          email: credential.email || 'apple_learning_user@privaterelay.appleid.com',
          name: credential.fullName ? 
            `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
            'Apple Learning User',
          full_name: credential.fullName ? 
            `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
            'Apple Learning User',
          first_name: credential.fullName?.givenName || 'Apple',
          last_name: credential.fullName?.familyName || 'User',
          user_type: 'learning',
          profile_type: 'learning',
          signup_method: 'apple',
        };

        // Store fallback authentication data
        await AsyncStorage.setItem('auth_token', 'apple_learning_signup_' + Date.now());
        await AsyncStorage.setItem('user_data', JSON.stringify(fallbackUser));

        // Store learning profile
        await AsyncStorage.setItem('learning_profile', JSON.stringify({
          education_level: 'Not specified',
          interests: 'General learning',
          learning_goals: 'Explore engineering topics',
          signup_date: new Date().toISOString(),
          signup_method: 'apple'
        }));

        console.log('✅ Apple fallback signup successful for learning');
        
        Alert.alert(
          'Welcome to JobEng Learning!',
          'Your account has been created with Apple. Complete your learning profile to get started!',
          [
            {
              text: 'Complete Profile',
              onPress: () => navigation.navigate('WelcomeLearningScreen')
            }
          ]
        );
      }
    } catch (error) {
      console.error('❌ Apple signup error:', error);
      if (error.code !== 'ERR_CANCELED') {
        Alert.alert('Apple Signup Error', 'Failed to complete Apple signup. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.container}>
            <View style={styles.headerContainer}>
              <Image
                source={require('../../../assets/jobeng_academy_logo.png')}
                style={styles.logo}
                
              />
              <Text style={styles.subtitle}>UK Engineering Excellence</Text>
            </View>

            <View style={styles.formContainer}>
              <Text style={styles.title}>Join Learning Portal</Text>
              <Text style={styles.description}>
                Start your engineering learning journey today
              </Text>



              {/* Full Name */}
              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.fullName && styles.inputWrapperFocused
                ]}>
                  <Ionicons 
                    name="person-outline" 
                    size={20} 
                    color={isFocused.fullName ? "#007AFF" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Full Name"
                    placeholderTextColor="#8E8E93"
                    value={formData.fullName}
                    onChangeText={(value) => handleInputChange('fullName', value)}
                    onFocus={() => handleFocus('fullName')}
                    onBlur={() => handleBlur('fullName')}
                    autoCapitalize="words"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Student Email */}
              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.studentEmail && styles.inputWrapperFocused
                ]}>
                  <Ionicons 
                    name="mail-outline" 
                    size={20} 
                    color={isFocused.studentEmail ? "#007AFF" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Student Email"
                    placeholderTextColor="#8E8E93"
                    value={formData.studentEmail}
                    onChangeText={(value) => handleInputChange('studentEmail', value)}
                    onFocus={() => handleFocus('studentEmail')}
                    onBlur={() => handleBlur('studentEmail')}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Education Level */}
              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.educationLevel && styles.inputWrapperFocused
                ]}>
                  <Ionicons 
                    name="school-outline" 
                    size={20} 
                    color={isFocused.educationLevel ? "#007AFF" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Education Level (e.g., High School, University)"
                    placeholderTextColor="#8E8E93"
                    value={formData.educationLevel}
                    onChangeText={(value) => handleInputChange('educationLevel', value)}
                    onFocus={() => handleFocus('educationLevel')}
                    onBlur={() => handleBlur('educationLevel')}
                    autoCapitalize="words"
                  />
                </View>
              </View>

              {/* Interests */}
              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.interests && styles.inputWrapperFocused
                ]}>
                  <Ionicons 
                    name="heart-outline" 
                    size={20} 
                    color={isFocused.interests ? "#007AFF" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Interests (e.g., Civil, Mechanical, Software)"
                    placeholderTextColor="#8E8E93"
                    value={formData.interests}
                    onChangeText={(value) => handleInputChange('interests', value)}
                    onFocus={() => handleFocus('interests')}
                    onBlur={() => handleBlur('interests')}
                    autoCapitalize="words"
                  />
                </View>
              </View>

              {/* Learning Goals */}
              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.learningGoals && styles.inputWrapperFocused
                ]}>
                  <Ionicons 
                    name="target-outline" 
                    size={20} 
                    color={isFocused.learningGoals ? "#007AFF" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Learning Goals"
                    placeholderTextColor="#8E8E93"
                    value={formData.learningGoals}
                    onChangeText={(value) => handleInputChange('learningGoals', value)}
                    onFocus={() => handleFocus('learningGoals')}
                    onBlur={() => handleBlur('learningGoals')}
                    autoCapitalize="sentences"
                  />
                </View>
              </View>

              {/* Password */}
              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.password && styles.inputWrapperFocused
                ]}>
                  <Ionicons 
                    name="lock-closed-outline" 
                    size={20} 
                    color={isFocused.password ? "#007AFF" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Password"
                    placeholderTextColor="#8E8E93"
                    value={formData.password}
                    onChangeText={(value) => handleInputChange('password', value)}
                    onFocus={() => handleFocus('password')}
                    onBlur={() => handleBlur('password')}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity
                    style={styles.passwordToggle}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Ionicons 
                      name={showPassword ? "eye-off" : "eye"} 
                      size={20} 
                      color="#8E8E93" 
                    />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Confirm Password */}
              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.confirmPassword && styles.inputWrapperFocused
                ]}>
                  <Ionicons 
                    name="lock-closed-outline" 
                    size={20} 
                    color={isFocused.confirmPassword ? "#007AFF" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Confirm Password"
                    placeholderTextColor="#8E8E93"
                    value={formData.confirmPassword}
                    onChangeText={(value) => handleInputChange('confirmPassword', value)}
                    onFocus={() => handleFocus('confirmPassword')}
                    onBlur={() => handleBlur('confirmPassword')}
                    secureTextEntry={!showConfirmPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity
                    style={styles.passwordToggle}
                    onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <Ionicons 
                      name={showConfirmPassword ? "eye-off" : "eye"} 
                      size={20} 
                      color="#8E8E93" 
                    />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Terms Agreement */}
              <View style={styles.termsContainer}>
                <TouchableOpacity 
                  style={styles.termsButton}
                  onPress={() => setAgreeToTerms(!agreeToTerms)}
                >
                  <Ionicons 
                    name={agreeToTerms ? "checkbox" : "square-outline"} 
                    size={20} 
                    color="#007AFF" 
                  />
                  <Text style={styles.termsText}>
                    I agree to the{' '}
                    <Text style={styles.termsLink}>Terms of Service</Text>
                    {' '}and{' '}
                    <Text style={styles.termsLink}>Privacy Policy</Text>
                  </Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity
                style={[styles.signupButton, isLoading && styles.signupButtonDisabled]}
                onPress={handleSignup}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator size={20} color="#FFFFFF" />
                ) : (
                  <>
                    <Text style={styles.signupButtonText}>Create Learning Account</Text>
                    <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
                  </>
                )}
              </TouchableOpacity>

              <View style={styles.footerContainer}>
                <Text style={styles.footerText}>
                  Already have an account?{' '}
                  <Text 
                    style={styles.footerLink}
                    onPress={() => navigation.navigate('LearningLoginScreen')}
                  >
                    Sign In
                  </Text>
                </Text>
                
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 40,
  },
  headerContainer: {
    alignItems: 'center',
    marginBottom: 40,
  },
  logo: {
    width: 300,
    height: 220,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: '#8E8E93',
    fontWeight: '500',
  },
  formContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    textAlign: 'center',
    marginBottom: 8,
  },
  description: {
    fontSize: 16,
    color: '#8E8E93',
    textAlign: 'center',
    marginBottom: 24,
  },
  socialSignupContainer: {
    marginBottom: 24,
  },
  socialButton: {
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  socialButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1C1C1E',
    marginLeft: 8,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E5E5EA',
  },
  dividerText: {
    marginHorizontal: 16,
    fontSize: 14,
    color: '#8E8E93',
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    paddingHorizontal: 16,
    height: 50,
  },
  inputWrapperFocused: {
    borderColor: '#007AFF',
    backgroundColor: '#FFFFFF',
  },
  inputIcon: {
    marginRight: 12,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1C1C1E',
  },
  passwordToggle: {
    padding: 4,
  },
  termsContainer: {
    marginBottom: 24,
  },
  termsButton: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  termsText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#1C1C1E',
    flex: 1,
    lineHeight: 20,
  },
  termsLink: {
    color: '#007AFF',
    fontWeight: '500',
  },
  signupButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  signupButtonDisabled: {
    opacity: 0.7,
  },
  signupButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 8,
  },
  footerContainer: {
    alignItems: 'center',
  },
  footerText: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 16,
  },
  footerLink: {
    color: '#007AFF',
    fontWeight: '500',
  },
  switchModeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  switchModeText: {
    fontSize: 14,
    color: '#007AFF',
    marginLeft: 4,
  },
});
